import React from "react";
import { Lock, ArrowLeft } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

interface ResultsAccessDeniedProps {
  message: string;
  pollSlug?: string;
  showVoteButton?: boolean;
  /**
   * Route de vote à utiliser. Les apps n'ont pas le même chemin :
   * date-polls expose `/poll/:slug/vote`, form-polls `/poll/:slug`.
   * Défaut : `/poll/:slug` (comportement historique).
   */
  voteHref?: string;
  /**
   * Le visiteur a déjà voté (get_poll_results_access.has_voted). Change le
   * libellé du CTA : « Modifier mon vote » au lieu de « Voter maintenant ».
   */
  hasVoted?: boolean;
  /**
   * Nombre de réponses sur le sondage, exposé même quand les résultats sont
   * masqués. Affiché en clair pour éviter le « 0 votant » trompeur.
   */
  responseCount?: number;
}

export function ResultsAccessDenied({
  message,
  pollSlug,
  showVoteButton = false,
  voteHref,
  hasVoted = false,
  responseCount,
}: ResultsAccessDeniedProps) {
  const navigate = useNavigate();
  const { t } = useTranslation("common");
  const showCount = typeof responseCount === "number" && responseCount > 0;

  return (
    <div className="min-h-screen-safe bg-background flex items-center justify-center p-4">
      <div className="max-w-md w-full">
        <div
          className="bg-card rounded-lg border border-border p-8 text-center"
          data-testid="results-access-denied"
        >
          {/* Icon */}
          <div className="flex justify-center mb-4">
            <div className="w-16 h-16 bg-warning/10 rounded-full flex items-center justify-center">
              <Lock className="w-8 h-8 text-warning" />
            </div>
          </div>

          {/* Title */}
          <h2 className="text-xl font-semibold text-foreground mb-2">
            {t("results_access.title")}
          </h2>

          {/* Message */}
          <p
            className={
              showCount
                ? "text-muted-foreground mb-2"
                : "text-muted-foreground mb-6"
            }
          >
            {message}
          </p>

          {/* Nombre de votes — affiché même verrouillé, pour ne jamais
              laisser croire que le sondage est vide. */}
          {showCount && (
            <p
              className="text-muted-foreground text-sm mb-6"
              data-testid="hidden-response-count"
            >
              {t("results_access.hidden_count", { count: responseCount })}
            </p>
          )}

          {/* Actions */}
          <div className="flex flex-col gap-3">
            {showVoteButton && pollSlug && (
              <button
                onClick={() => navigate(voteHref ?? `/poll/${pollSlug}`)}
                className="w-full px-6 py-3 bg-primary hover:bg-primary/90 text-primary-foreground rounded-lg font-medium transition-colors"
                data-testid="resultsaccessdenied-voter-maintenant"
              >
                {hasVoted
                  ? t("results_access.edit_my_vote")
                  : t("results_access.vote_now")}
              </button>
            )}

            <button
              onClick={() => navigate(-1)}
              className="w-full px-6 py-3 bg-muted hover:bg-accent text-muted-foreground rounded-lg font-medium transition-colors flex items-center justify-center gap-2"
              data-testid="resultsaccessdenied-navigate"
            >
              <ArrowLeft className="w-4 h-4" />
              {t("results_access.back")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
